import classNames from "classnames";
import groupBy from "lodash/groupBy";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline } from "PFComponents/containers";
import { SelectPill } from "PFComponents/double_section_pill/select_pill";
import { SelectV2 } from "PFComponents/select_v2";
import { Typography } from "PFComponents/typography";
import { canAddMatchableTypeValue, canEditOrRemoveMatchableTypeValues } from "PFCore/helpers/activities";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { fetchAutocompleteCustomValues } from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import { Importance } from "PFTypes";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./keywords_sliders.module.scss";

const TypeSection = ({
  remove,
  handleChange,
  search,
  pushWithNewImportance,
  type,
  keywords,
  error,
  initialOpen = false,
  importanceOptions,
  onChangeOpen
}) => {
  const { t } = useTranslation("search");
  const [open, setOpen] = useState(initialOpen);

  const notChosen = (option) => !keywords.find(({ id }) => id === option.id);

  const handleOpen = (open) => {
    setOpen(open);
    onChangeOpen?.(open);
  };

  return (
    <li
      onClick={open ? null : () => handleOpen(true)}
      onKeyDown={open ? null : () => handleOpen(true)}
      className={classNames(css.section, { [css.sectionClosed]: !open, [css.error]: error })}
    >
      <Inline justifyContent="space-between" alignItems="center">
        <Typography variant="bodyRegular">
          {type.display_as} ({keywords.length}) {!search && ` - ${t("parts.keywords.editOriginalWarn")}`}
        </Typography>
        <ActionIcon
          size="lg"
          name={open ? "cross" : "add"}
          color="palettePrimary2"
          onClick={() => handleOpen(!open)}
        />
      </Inline>
      {error && <span className={css.errorMessage}>{error}</span>}
      {open && (
        <section className={css.sectionList}>
          {handleChange && (
            <SelectV2
              fetchOptions={({ searchTerm: term }) => {
                if (!term) {
                  return Promise.resolve({ entries: [], meta: { total: 0 } });
                }
                return fetchAutocompleteCustomValues({ type: type.name, ...(term ? { term } : {}) }).then(
                  (options) => ({
                    entries: options.map((option) => ({
                      id: option.id,
                      value: option.text,
                      original: { ...option, type, value: option.text }
                    })),
                    meta: { total: options.length }
                  })
                );
              }}
              values={[]}
              parseOptions={(options) => options.filter(notChosen)}
              onChange={handleChange}
              placeholder={t("parts.keywords.addAttribute")}
            />
          )}
          <div className={css.pillsList}>
            {keywords.map((keyword) => (
              <SelectPill
                key={keyword.id}
                leftContent={String(keyword.value)}
                attribute={
                  importanceOptions.find(({ item }) => item === keyword.importance)?.displayElement || ""
                }
                attributeOptions={importanceOptions}
                onAttributeChange={(newImportance) => pushWithNewImportance(keyword, newImportance)}
                onDelete={remove ? () => remove(keyword) : null}
              />
            ))}
          </div>
        </section>
      )}
    </li>
  );
};

TypeSection.propTypes = {
  type: PropTypes.shape({
    display_as: PropTypes.string,
    name: PropTypes.string,
    id: PropTypes.number
  }).isRequired,
  keywords: PropTypes.array,
  handleChange: PropTypes.func,
  remove: PropTypes.func,
  search: PropTypes.bool,
  pushWithNewImportance: PropTypes.func,
  importanceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      item: PropTypes.number,
      displayElement: PropTypes.any
    })
  ).isRequired,
  error: PropTypes.string,
  initialOpen: PropTypes.bool,
  onChangeOpen: PropTypes.func
};

const KeywordsSliders = ({
  remove,
  search,
  pushWithNewImportance,
  handleAddOne,
  choosenKeywords,
  matchableTypes,
  style,
  errors,
  typesOpened = [],
  onChangeOpen
}) => {
  const { customTypes } = useCustomTypes();
  const { t } = useTranslation("search");

  const matchableTypeNames = matchableTypes.map((type) => type.name);

  const keywords = choosenKeywords.filter(({ type }) => matchableTypeNames.includes(type.name));

  const notChosen = (option) => !keywords.find(({ id }) => id === option.id);

  const keywordsByType = groupBy(keywords, (keyword) => keyword.type.id);
  const typesToDisplay = customTypes
    .filter((type) => matchableTypeNames.includes(type.name))
    .sort((typeA, typeB) => (typeA.display_as > typeB.display_as ? 1 : -1));

  const importanceOptions = useMemo(
    () => [
      {
        id: Importance.Supporting,
        item: Importance.Supporting,
        displayElement: t("parts.keywords.supporting")
      },
      { id: Importance.Essential, item: Importance.Essential, displayElement: t("parts.keywords.essential") },
      { id: Importance.Required, item: Importance.Required, displayElement: t("parts.keywords.hardFilter") }
    ],
    [t]
  );

  return (
    <div style={style}>
      {typesToDisplay.length > 0 && (
        <ul className={css.list}>
          {typesToDisplay.map((type) => (
            <TypeSection
              key={type.id}
              search={search}
              pushWithNewImportance={pushWithNewImportance}
              remove={canEditOrRemoveMatchableTypeValues(type, matchableTypes) ? remove : null}
              type={type}
              handleChange={
                canAddMatchableTypeValue(type, matchableTypes)
                  ? (option) => {
                      if (notChosen(option)) {
                        handleAddOne({ ...option.original, text: option.value });
                      }
                    }
                  : null
              }
              keywords={keywordsByType[type.id] || []}
              error={errors && errors[type.name]}
              initialOpen={typesOpened.includes(type.name)}
              importanceOptions={importanceOptions}
              onChangeOpen={(open) => onChangeOpen?.(open, type.name)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

KeywordsSliders.propTypes = {
  remove: PropTypes.func,
  search: PropTypes.bool,
  pushWithNewImportance: PropTypes.func,
  handleAddOne: PropTypes.func,
  choosenKeywords: PropTypes.array,
  matchableTypes: PropTypes.arrayOf(
    PropTypes.shape({
      compliance_field: PropTypes.bool,
      name: PropTypes.string.isRequired
    })
  ),
  style: PropTypes.object,
  errors: PropTypes.object,
  typesOpened: PropTypes.arrayOf(PropTypes.string),
  onChangeOpen: PropTypes.func
};

export default KeywordsSliders;
